// Minimal cookie helpers for reading/writing non-HttpOnly cookie markers

export const CONSENT_COOKIE = 'cookie_consent';

// Guest-level consent keys (Phase I)
export const GUEST_CONSENT_STATUS = 'consent_status'; // ACCEPTED | REJECTED | CUSTOM
export const GUEST_CONSENT_CATEGORIES = 'consent_categories'; // JSON string array
export const GUEST_CONSENT_DATE = 'consent_date'; // ISO timestamp
export const GUEST_CONSENT_POLICY_VERSION = 'consent_policy_version'; // string

export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^|; )' + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1') + '=([^;]*)'));
  return match ? decodeURIComponent(match[2]) : null;
}

export function setCookie(name: string, value: string, days = 365, domain?: string) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; expires=${expires}`;
  if (domain) cookie += `; domain=${domain}`;
  // Leave SameSite/secure flags to backend if it also sets; here only for local dev
  document.cookie = cookie;
}

export function deleteCookie(name: string, domain?: string) {
  if (typeof document === 'undefined') return;
  let cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

// ---------- Guest consent helpers ----------
export type GuestConsentStatus = 'ACCEPTED' | 'REJECTED' | 'CUSTOM';

export interface GuestConsentRecord {
  status: GuestConsentStatus;
  categories: string[]; // e.g., ['analytics','marketing']
  date: string; // ISO timestamp
}

export function saveGuestConsent(status: GuestConsentStatus, categories: string[], date?: string, policyVersion?: string) {
  const ts = date || new Date().toISOString();
  
  // Create cookie_consent JSON object that backend expects
  const consentObj = {
    necessary: true,
    functional: categories.includes('functional'),
    analytics: categories.includes('analytics'),
    marketing: categories.includes('marketing')
  };
  
  // Set all consent cookies (12 months)
  setCookie(CONSENT_COOKIE, JSON.stringify(consentObj), 365);
  setCookie(GUEST_CONSENT_STATUS, status, 365);
  setCookie(GUEST_CONSENT_CATEGORIES, JSON.stringify(categories || []), 365);
  setCookie(GUEST_CONSENT_DATE, ts, 365);
  if (policyVersion) setCookie(GUEST_CONSENT_POLICY_VERSION, policyVersion, 365);
  
  // localStorage mirror for robustness/auditing UI
  try {
    localStorage.setItem(CONSENT_COOKIE, JSON.stringify(consentObj));
    localStorage.setItem(GUEST_CONSENT_STATUS, status);
    localStorage.setItem(GUEST_CONSENT_CATEGORIES, JSON.stringify(categories || []));
    localStorage.setItem(GUEST_CONSENT_DATE, ts);
    if (policyVersion) localStorage.setItem(GUEST_CONSENT_POLICY_VERSION, policyVersion);
  } catch { /* ignore quota */ }
}

export function readGuestConsent(): GuestConsentRecord | null {
  // Cookie is the source of truth for 12-month validity; if cookie missing/expired, treat as no consent
  const status = readCookie(GUEST_CONSENT_STATUS);
  if (!status) return null;
  let categories: string[] = [];
  const catRaw = readCookie(GUEST_CONSENT_CATEGORIES) || (typeof localStorage !== 'undefined' ? localStorage.getItem(GUEST_CONSENT_CATEGORIES) : null);
  if (catRaw) {
    try {
      const parsed = JSON.parse(catRaw);
      if (Array.isArray(parsed)) categories = parsed.map((s: any) => String(s));
    } catch {
      // allow comma-separated fallback
      categories = String(catRaw).split(',').map(s => s.trim()).filter(Boolean);
    }
  }
  const date = readCookie(GUEST_CONSENT_DATE) || (typeof localStorage !== 'undefined' ? localStorage.getItem(GUEST_CONSENT_DATE) : null) || new Date().toISOString();
  return { status: status as GuestConsentStatus, categories, date };
}

export function deleteGuestConsent() {
  deleteCookie(CONSENT_COOKIE);
  deleteCookie(GUEST_CONSENT_STATUS);
  deleteCookie(GUEST_CONSENT_CATEGORIES);
  deleteCookie(GUEST_CONSENT_DATE);
  deleteCookie(GUEST_CONSENT_POLICY_VERSION);
  try {
    localStorage.removeItem(CONSENT_COOKIE);
    localStorage.removeItem(GUEST_CONSENT_STATUS);
    localStorage.removeItem(GUEST_CONSENT_CATEGORIES);
    localStorage.removeItem(GUEST_CONSENT_DATE);
    localStorage.removeItem(GUEST_CONSENT_POLICY_VERSION);
  } catch { /* ignore */ }
}
